import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  MapPin,
  User,
  Calendar,
  FileText,
  CheckCircle2,
  ArrowRight,
  Hash,
  DollarSign,
  Clock,
  Package,
  Scale,
  AlertTriangle,
  Info,
  Sparkles,
  Loader2,
  Building2,
  ChevronDown,
  AlertCircle,
} from 'lucide-react';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { useAddressSearch } from '@/hooks/useAddressAutocomplete';
import {
  useCreateCustomerOrder,
  useDeliveryEstimate,
  type CustomerCreateOrderRequest,
  type CustomerOrganization,
} from '@cfleet/api-client';

interface CreateOrderProps {
  onCancel: () => void;
  onSubmit: () => void;
  organizations: CustomerOrganization[];
  initialOrgId?: string;
}

// Reusable input component
const InputGroup = ({ label, placeholder, icon: Icon, type = 'text', ...props }: any) => (
  <div className="relative group">
    <div className="absolute left-3 top-2.5 text-zinc-400 group-focus-within:text-blue-500 transition-colors">
      <Icon size={14} />
    </div>
    <input
      type={type}
      className="w-full bg-white border border-zinc-200 rounded-md py-2 pl-9 pr-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all shadow-sm"
      placeholder={placeholder}
      {...props}
    />
  </div>
);

const SectionHeader = ({
  title,
  colorClass,
  icon: Icon,
}: {
  title: string;
  colorClass: string;
  icon?: any;
}) => (
  <div className="flex items-center gap-2 mb-4">
    <div className={`w-2 h-2 rounded-full ${colorClass}`}></div>
    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
      {title}
      {Icon && <Icon size={12} className="text-zinc-400" />}
    </h3>
  </div>
);

export const CreateOrder: React.FC<CreateOrderProps> = ({
  onCancel,
  onSubmit,
  organizations,
  initialOrgId,
}) => {
  const authConfig = useAuthConfig();

  // API hooks
  const {
    mutate: createOrder,
    isPending: isCreating,
    error: createError,
  } = useCreateCustomerOrder({ authConfig });

  // Target Organization State - Priority: Prop -> Active Org -> First Org
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    initialOrgId || organizations.find((o) => o.status === 'active')?.id || ''
  );

  // Sync state if initialOrgId changes (e.g. starting a new order while already on create view)
  useEffect(() => {
    if (initialOrgId) {
      setSelectedOrgId(initialOrgId);
    }
  }, [initialOrgId]);

  // State for Order Details
  const [orderId, setOrderId] = useState('');
  const [createdAt, setCreatedAt] = useState(new Date().toISOString().slice(0, 16));

  // Order Content State
  const [itemDescription, setItemDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [weight, setWeight] = useState('');

  // Pickup Address State
  const [pickupStreet, setPickupStreet] = useState('');
  const [pickupUnit, setPickupUnit] = useState('');
  const [pickupCity, setPickupCity] = useState('');
  const [pickupProvince, setPickupProvince] = useState('');
  const [pickupZip, setPickupZip] = useState('');
  const [pickupContactName, setPickupContactName] = useState('');
  const [pickupContactPhone, setPickupContactPhone] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupNotes, setPickupNotes] = useState('');

  // Dropoff Address State
  const [dropoffStreet, setDropoffStreet] = useState('');
  const [dropoffUnit, setDropoffUnit] = useState('');
  const [dropoffCity, setDropoffCity] = useState('');
  const [dropoffProvince, setDropoffProvince] = useState('');
  const [dropoffZip, setDropoffZip] = useState('');
  const [dropoffContactName, setDropoffContactName] = useState('');
  const [dropoffContactPhone, setDropoffContactPhone] = useState('');
  const [dropoffNotes, setDropoffNotes] = useState('');

  // Coordinates from autocomplete (cleared on manual edit)
  const [pickupCoords, setPickupCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [dropoffCoords, setDropoffCoords] = useState<{ lat: number; lon: number } | null>(null);

  // Address autocomplete
  const pickupAutocomplete = useAddressSearch();
  const dropoffAutocomplete = useAddressSearch();
  const pickupDropdownRef = useRef<HTMLDivElement>(null);
  const dropoffDropdownRef = useRef<HTMLDivElement>(null);

  // Delivery Requirements
  const [requirePhoto, setRequirePhoto] = useState(false);
  const [requireSignature, setRequireSignature] = useState(false);
  const [requireAgeVerification, setRequireAgeVerification] = useState(false);

  // Handling / Caution State
  const [cautionNotes, setCautionNotes] = useState('');
  const [cautionTags, setCautionTags] = useState<string[]>([]);

  // Form error state
  const [formError, setFormError] = useState<string | null>(null);

  // Debounced estimate request — builds after both addresses stabilize (1500ms)
  const [debouncedEstimateRequest, setDebouncedEstimateRequest] = useState<{
    pickup_address: { street: string; unit?: string; city: string; province?: string; zip: string; latitude?: number; longitude?: number };
    dropoff_address: { street: string; unit?: string; city: string; province?: string; zip: string; latitude?: number; longitude?: number };
  } | null>(null);

  const addressFingerprint = useMemo(() => {
    if (!pickupStreet || !pickupCity || !dropoffStreet || !dropoffCity) return null;
    return `${pickupStreet}|${pickupCity}|${pickupZip}|${dropoffStreet}|${dropoffCity}|${dropoffZip}`;
  }, [pickupStreet, pickupCity, pickupZip, dropoffStreet, dropoffCity, dropoffZip]);

  useEffect(() => {
    if (!addressFingerprint) {
      setDebouncedEstimateRequest(null);
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedEstimateRequest({
        pickup_address: {
          street: pickupStreet,
          unit: pickupUnit || undefined,
          city: pickupCity,
          province: pickupProvince || undefined,
          zip: pickupZip,
          latitude: pickupCoords?.lat,
          longitude: pickupCoords?.lon,
        },
        dropoff_address: {
          street: dropoffStreet,
          unit: dropoffUnit || undefined,
          city: dropoffCity,
          province: dropoffProvince || undefined,
          zip: dropoffZip,
          latitude: dropoffCoords?.lat,
          longitude: dropoffCoords?.lon,
        },
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [addressFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: estimateData, isPending: isEstimating } = useDeliveryEstimate(
    debouncedEstimateRequest,
    { authConfig }
  );

  // Format delivery window from API response
  const estimatedDelivery =
    estimateData?.estimated_date_min && estimateData?.estimated_date_max
      ? (() => {
          try {
            const fmt = (d: string) =>
              new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            return `${fmt(estimateData.estimated_date_min)} - ${fmt(estimateData.estimated_date_max)}`;
          } catch {
            return null;
          }
        })()
      : null;

  // Client-side Haversine estimate when both addresses have coordinates
  const haversineEstimateKm = useMemo(() => {
    if (!pickupCoords || !dropoffCoords) return null;
    const R = 6371;
    const dLat = ((dropoffCoords.lat - pickupCoords.lat) * Math.PI) / 180;
    const dLon = ((dropoffCoords.lon - pickupCoords.lon) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((pickupCoords.lat * Math.PI) / 180) *
        Math.cos((dropoffCoords.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }, [pickupCoords, dropoffCoords]);

  // Calculate estimated price: API response > client-side Haversine > 0
  const haversinePrice = haversineEstimateKm != null ? 15 + haversineEstimateKm * 2.5 : null;
  const estimatedPrice = estimateData?.price ?? haversinePrice ?? 0;

  const toggleCautionTag = (tag: string) => {
    if (cautionTags.includes(tag)) {
      setCautionTags(cautionTags.filter((t) => t !== tag));
    } else {
      setCautionTags([...cautionTags, tag]);
    }
  };

  const activeOrganizations = organizations.filter((o) => o.status === 'active');

  // Map caution tags to API format
  const mapCautionTagsToApi = (
    tags: string[]
  ): ('Fragile' | 'Perishable' | 'Heavy' | 'Oversized')[] => {
    const validTags: ('Fragile' | 'Perishable' | 'Heavy' | 'Oversized')[] = [];
    for (const tag of tags) {
      if (tag === 'Fragile' || tag === 'Perishable') {
        validTags.push(tag);
      }
      // Map "Keep Upright" and "Do Not Stack" to Fragile if needed
      if (tag === 'Keep Upright' || tag === 'Do Not Stack') {
        if (!validTags.includes('Fragile')) {
          validTags.push('Fragile');
        }
      }
    }
    return validTags;
  };

  const handleSubmit = () => {
    setFormError(null);

    if (!selectedOrgId) {
      setFormError('Please select a delivery organization.');
      return;
    }

    // Validate required fields
    if (!pickupStreet || !pickupCity || !pickupZip) {
      setFormError('Please provide complete pickup address details.');
      return;
    }

    if (!pickupContactName || !pickupContactPhone) {
      setFormError('Please provide pickup contact information.');
      return;
    }

    if (!dropoffStreet || !dropoffCity || !dropoffZip) {
      setFormError('Please provide complete dropoff address details.');
      return;
    }

    if (!dropoffContactName || !dropoffContactPhone) {
      setFormError('Please provide dropoff contact information.');
      return;
    }

    // Build the API request
    const orderData: CustomerCreateOrderRequest = {
      organization_id: selectedOrgId,
      pickup: {
        address: {
          street: pickupStreet,
          unit: pickupUnit || undefined,
          city: pickupCity,
          province: pickupProvince || undefined,
          zip: pickupZip,
          latitude: pickupCoords?.lat,
          longitude: pickupCoords?.lon,
        },
        contact: {
          name: pickupContactName,
          phone: pickupContactPhone,
        },
        notes: pickupNotes || undefined,
        time: pickupTime ? new Date(pickupTime).toISOString() : undefined,
      },
      dropoff: {
        address: {
          street: dropoffStreet,
          unit: dropoffUnit || undefined,
          city: dropoffCity,
          province: dropoffProvince || undefined,
          zip: dropoffZip,
          latitude: dropoffCoords?.lat,
          longitude: dropoffCoords?.lon,
        },
        contact: {
          name: dropoffContactName,
          phone: dropoffContactPhone,
        },
        notes: dropoffNotes || undefined,
      },
      items: {
        description: itemDescription || undefined,
        quantity: parseInt(quantity, 10) || 1,
        weight: weight ? `${weight} lbs` : undefined,
      },
      handling:
        cautionTags.length > 0 || cautionNotes
          ? {
              caution_notes: cautionNotes || undefined,
              tags: mapCautionTagsToApi(cautionTags),
            }
          : undefined,
    };

    createOrder(orderData, {
      onSuccess: () => {
        onSubmit();
      },
      onError: (error) => {
        setFormError(error.message || 'Failed to create order. Please try again.');
      },
    });
  };

  return (
    <div className="flex flex-col h-full bg-white text-zinc-900">
      {/* Header */}
      <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="text-zinc-500 hover:text-zinc-800 transition-colors"
          >
            <ArrowRight size={16} className="rotate-180" />
          </button>
          <h2 className="text-sm font-semibold text-zinc-900">New Delivery Order</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="px-3 py-1 bg-zinc-50 rounded border border-zinc-200 text-xs text-zinc-500">
            Est. Price:{' '}
            <span className="text-zinc-900 font-mono ml-1 font-medium">
              ${Number(estimatedPrice || 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {(formError || createError) && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle size={16} />
          <span>{formError || createError?.message}</span>
        </div>
      )}

      {/* Main Form Content - Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Configuration & Order Details */}
        <div className="flex-1 p-6 border-r border-zinc-200 overflow-y-auto custom-scrollbar">
          <div className="max-w-md mx-auto space-y-8">
            {/* REQUIRED: Organization Selection */}
            <div className="p-4 bg-blue-50/50 border border-blue-100 rounded-xl space-y-3">
              <SectionHeader title="Dispatch To" colorClass="bg-blue-600" icon={Building2} />
              <div className="relative group">
                <div className="absolute left-3 top-2.5 text-blue-500">
                  <Building2 size={16} />
                </div>
                <select
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="w-full bg-white border border-blue-200 rounded-lg py-2 pl-10 pr-10 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all appearance-none cursor-pointer font-medium"
                >
                  <option value="" disabled>
                    Select Organization
                  </option>
                  {activeOrganizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                  {organizations
                    .filter((o) => o.status === 'disabled')
                    .map((org) => (
                      <option key={org.id} value={org.id} disabled>
                        {org.name} (Disabled)
                      </option>
                    ))}
                </select>
                <div className="absolute right-3 top-2.5 text-zinc-400 pointer-events-none">
                  <ChevronDown size={16} />
                </div>
              </div>
              <p className="text-[10px] text-zinc-500 italic pl-1">
                Select which delivery partner will receive this order.
              </p>
            </div>

            {/* Order Details Section */}
            <div>
              <SectionHeader title="Order Details" colorClass="bg-zinc-400" />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Reference ID (optional)
                  </label>
                  <InputGroup
                    icon={Hash}
                    placeholder="Your reference"
                    value={orderId}
                    onChange={(e: any) => setOrderId(e.target.value)}
                  />
                </div>
                <div className="col-span-2 space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">Created At</label>
                  <div className="relative group">
                    <div className="absolute left-3 top-2.5 text-zinc-400">
                      <Clock size={14} />
                    </div>
                    <input
                      type="datetime-local"
                      value={createdAt}
                      onChange={(e) => setCreatedAt(e.target.value)}
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 pl-9 pr-3 text-sm text-zinc-700 focus:outline-none focus:border-zinc-400 transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Order Content */}
            <div>
              <SectionHeader title="Order Content" colorClass="bg-zinc-500" icon={Package} />
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Item Description
                  </label>
                  <InputGroup
                    icon={Info}
                    placeholder="E.g. Electronics, Documents"
                    value={itemDescription}
                    onChange={(e: any) => setItemDescription(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">Quantity</label>
                    <InputGroup
                      icon={Package}
                      type="number"
                      placeholder="1"
                      value={quantity}
                      onChange={(e: any) => setQuantity(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">
                      Weight (lbs)
                    </label>
                    <InputGroup
                      icon={Scale}
                      placeholder="Optional"
                      value={weight}
                      onChange={(e: any) => setWeight(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pickup Details */}
            <div>
              <SectionHeader title="Pickup Details" colorClass="bg-zinc-600" />
              <div className="space-y-4">
                <div className="space-y-1.5 relative" ref={pickupDropdownRef}>
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Street Address *
                  </label>
                  <div className="relative group">
                    <div className="absolute left-3 top-2.5 text-zinc-400 group-focus-within:text-blue-500 transition-colors">
                      <MapPin size={14} />
                    </div>
                    <input
                      type="text"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 pl-9 pr-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all shadow-sm"
                      placeholder="123 Main Street"
                      value={pickupStreet}
                      onChange={(e) => {
                        setPickupStreet(e.target.value);
                        setPickupCoords(null);
                        pickupAutocomplete.setQuery(e.target.value);
                      }}
                      onFocus={() => {
                        if (pickupAutocomplete.suggestions.length > 0) {
                          pickupAutocomplete.setShowDropdown(true);
                        }
                      }}
                    />
                    {pickupAutocomplete.isLoading && (
                      <div className="absolute right-3 top-2.5">
                        <Loader2 size={14} className="animate-spin text-zinc-400" />
                      </div>
                    )}
                  </div>
                  {pickupAutocomplete.showDropdown && pickupAutocomplete.suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {pickupAutocomplete.suggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-zinc-100 last:border-0"
                          onClick={() => {
                            setPickupStreet(s.address.street);
                            setPickupUnit(s.address.unit || '');
                            setPickupCity(s.address.city);
                            setPickupProvince(s.address.province || '');
                            setPickupZip(s.address.zip);
                            setPickupCoords(
                              s.latitude != null && s.longitude != null
                                ? { lat: s.latitude, lon: s.longitude }
                                : null
                            );
                            pickupAutocomplete.setQuery('');
                            pickupAutocomplete.setShowDropdown(false);
                          }}
                        >
                          <span className="text-zinc-900">{s.formatted_address}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Unit / Suite (optional)
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                    placeholder="Suite 400"
                    value={pickupUnit}
                    onChange={(e) => setPickupUnit(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">City *</label>
                    <input
                      type="text"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                      placeholder="Toronto"
                      value={pickupCity}
                      onChange={(e) => setPickupCity(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">Province</label>
                    <input
                      type="text"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                      placeholder="ON"
                      value={pickupProvince}
                      onChange={(e) => setPickupProvince(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">
                      Postal Code *
                    </label>
                    <input
                      type="text"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                      placeholder="M5J 2N8"
                      value={pickupZip}
                      onChange={(e) => setPickupZip(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">
                      Contact Name *
                    </label>
                    <InputGroup
                      icon={User}
                      placeholder="Sender Name"
                      value={pickupContactName}
                      onChange={(e: any) => setPickupContactName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">Phone *</label>
                    <InputGroup
                      icon={FileText}
                      placeholder="+1 (555) 000-0000"
                      type="tel"
                      value={pickupContactPhone}
                      onChange={(e: any) => setPickupContactPhone(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Pickup Time (optional)
                  </label>
                  <div className="relative">
                    <Calendar size={14} className="absolute left-3 top-2.5 text-zinc-400" />
                    <input
                      type="datetime-local"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 pl-9 pr-3 text-sm text-zinc-700"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Pickup Notes (optional)
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                    placeholder="E.g. Call on arrival"
                    value={pickupNotes}
                    onChange={(e) => setPickupNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Dropoff & Caution */}
        <div className="flex-1 p-6 overflow-y-auto custom-scrollbar bg-zinc-50/50">
          <div className="max-w-md mx-auto space-y-8">
            <div>
              <SectionHeader title="Dropoff Details" colorClass="bg-blue-500" />
              <div className="space-y-4">
                <div className="space-y-1.5 relative" ref={dropoffDropdownRef}>
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Street Address *
                  </label>
                  <div className="relative group">
                    <div className="absolute left-3 top-2.5 text-zinc-400 group-focus-within:text-blue-500 transition-colors">
                      <MapPin size={14} />
                    </div>
                    <input
                      type="text"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 pl-9 pr-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all shadow-sm"
                      placeholder="456 Oak Avenue"
                      value={dropoffStreet}
                      onChange={(e) => {
                        setDropoffStreet(e.target.value);
                        setDropoffCoords(null);
                        dropoffAutocomplete.setQuery(e.target.value);
                      }}
                      onFocus={() => {
                        if (dropoffAutocomplete.suggestions.length > 0) {
                          dropoffAutocomplete.setShowDropdown(true);
                        }
                      }}
                    />
                    {dropoffAutocomplete.isLoading && (
                      <div className="absolute right-3 top-2.5">
                        <Loader2 size={14} className="animate-spin text-zinc-400" />
                      </div>
                    )}
                  </div>
                  {dropoffAutocomplete.showDropdown && dropoffAutocomplete.suggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {dropoffAutocomplete.suggestions.map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors border-b border-zinc-100 last:border-0"
                          onClick={() => {
                            setDropoffStreet(s.address.street);
                            setDropoffUnit(s.address.unit || '');
                            setDropoffCity(s.address.city);
                            setDropoffProvince(s.address.province || '');
                            setDropoffZip(s.address.zip);
                            setDropoffCoords(
                              s.latitude != null && s.longitude != null
                                ? { lat: s.latitude, lon: s.longitude }
                                : null
                            );
                            dropoffAutocomplete.setQuery('');
                            dropoffAutocomplete.setShowDropdown(false);
                          }}
                        >
                          <span className="text-zinc-900">{s.formatted_address}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Unit / Suite (optional)
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                    placeholder="Apt 2B"
                    value={dropoffUnit}
                    onChange={(e) => setDropoffUnit(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">City *</label>
                    <input
                      type="text"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                      placeholder="Toronto"
                      value={dropoffCity}
                      onChange={(e) => setDropoffCity(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">Province</label>
                    <input
                      type="text"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                      placeholder="ON"
                      value={dropoffProvince}
                      onChange={(e) => setDropoffProvince(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">
                      Postal Code *
                    </label>
                    <input
                      type="text"
                      className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                      placeholder="M4K 1P5"
                      value={dropoffZip}
                      onChange={(e) => setDropoffZip(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">
                      Recipient Name *
                    </label>
                    <InputGroup
                      icon={User}
                      placeholder="Recipient Name"
                      value={dropoffContactName}
                      onChange={(e: any) => setDropoffContactName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-medium text-zinc-500 ml-1">Phone *</label>
                    <InputGroup
                      icon={FileText}
                      placeholder="+1 (555) 000-0000"
                      type="tel"
                      value={dropoffContactPhone}
                      onChange={(e: any) => setDropoffContactPhone(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Delivery Notes (optional)
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                    placeholder="E.g. Leave at reception"
                    value={dropoffNotes}
                    onChange={(e) => setDropoffNotes(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">Requirements</label>
                  <div className="flex flex-col gap-2 p-3 border border-zinc-200 rounded-md bg-white shadow-sm">
                    {[
                      {
                        label: 'Proof of delivery (Photo)',
                        checked: requirePhoto,
                        onChange: setRequirePhoto,
                      },
                      {
                        label: 'Signature Required',
                        checked: requireSignature,
                        onChange: setRequireSignature,
                      },
                      {
                        label: 'Age Verification (21+)',
                        checked: requireAgeVerification,
                        onChange: setRequireAgeVerification,
                      },
                    ].map((req) => (
                      <label
                        key={req.label}
                        className="flex items-center gap-2 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={req.checked}
                          onChange={(e) => req.onChange(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-zinc-300 text-blue-600"
                        />
                        <span className="text-xs text-zinc-600 group-hover:text-zinc-900">
                          {req.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <SectionHeader
                title="Caution & Handling"
                colorClass="bg-amber-500"
                icon={AlertTriangle}
              />
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Handling Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['Fragile', 'Perishable', 'Keep Upright', 'Do Not Stack'].map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => toggleCautionTag(tag)}
                        className={`px-3 py-1.5 rounded text-xs font-medium border transition-all ${cautionTags.includes(tag) ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-white text-zinc-600 border-zinc-200'}`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] font-medium text-zinc-500 ml-1">
                    Special Instructions
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white border border-zinc-200 rounded-md py-2 px-3 text-sm text-zinc-900 focus:outline-none focus:border-amber-500 transition-all shadow-sm"
                    placeholder="E.g. Contains fragile electronics"
                    value={cautionNotes}
                    onChange={(e) => setCautionNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="h-16 border-t border-zinc-200 bg-white flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          {isEstimating && (
            <div className="flex items-center gap-2 text-xs text-blue-600 animate-pulse bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100">
              <Loader2 size={12} className="animate-spin" />
              <span>Calculating logistics...</span>
            </div>
          )}
          {!isEstimating && estimatedDelivery && (
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100">
              <Sparkles size={12} />
              <span className="font-medium">Estimated Delivery: {estimatedDelivery}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            disabled={isCreating}
            className="px-4 py-2 rounded-md text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isCreating}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <CheckCircle2 size={16} className="text-white" />
                Place Order
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
